import { useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { css, keyframes } from '@emotion/react';
import styled from '@emotion/styled';
import { PreviousButton } from './PreviousButton';
import { TextButton } from './TextButton';
import { NextButton } from './NextButton';
import { Counter } from './Counter';

const nextRecordingAnimation = keyframes`
  0% {
    background-position-x: 0%;
  }
  100% {
    background-position-x: -100%;
  }
`;

const footerStyles = css`
  margin-top: auto;
  height: 45px;
  display: flex;
  align-items: center;
  padding: 0 32px;
  justify-content: space-between;
  background-color: rgba(255, 255, 255, 0.1);
  font-size: 12px;
`;

const AnimatedFooter = styled.div`
  ${footerStyles};
  animation-duration: ${(props) => props.timer || 8}s;
  animation-fill-mode: forwards;
  animation-iteration-count: 1;
  animation-timing-function: linear;
  animation-name: ${nextRecordingAnimation};

  background: linear-gradient(to right, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.2));
  background-size: 200%;
`;

export const Footer = ({
  timer,
  isAutoplaying,
  isCancelledNextRecordingCountdown,
  onCancelNextRecording,
  onReplay,
  isTokenShared,
  hasPreviousRecording,
  onPrevious,
  hasNextRecording,
  onTimerExpired,
  onNext,
}) => {
  const [hasTimerExpired, setHasTimerExpired] = useState(false);
  const { t } = useTranslation();
  if (!isCancelledNextRecordingCountdown && isAutoplaying && !isTokenShared && hasNextRecording) {
    return (
      <AnimatedFooter timer={timer}>
        <PreviousButton disabled={!hasPreviousRecording} onClick={onPrevious} />
        <span>
          <span
            css={css`
              opacity: 0.7;
            `}
          >
            {t('common:player.next-recording-in')}{' '}
            <Counter
              timer={8}
              onTimerExpired={() => {
                onTimerExpired();
                setHasTimerExpired(true);
              }}
            />
            {!hasTimerExpired && <span> • </span>}
          </span>
          {!hasTimerExpired && (
            <TextButton onClick={onCancelNextRecording}>{t('common:player.cancel')}</TextButton>
          )}
        </span>
        <NextButton disabled={!hasNextRecording} onClick={onNext} />
      </AnimatedFooter>
    );
  }
  return (
    <div css={footerStyles}>
      <PreviousButton disabled={!hasPreviousRecording || isTokenShared} onClick={onPrevious} />
      <TextButton onClick={onReplay}>{t('common:player.replay')}</TextButton>
      <NextButton disabled={!hasNextRecording || isTokenShared} onClick={onNext} />
    </div>
  );
};

Footer.defaultProps = {
  hasPreviousRecording: false,
  hasNextRecording: false,
  onTimerExpired: () => {},
};

Footer.propTypes = {
  timer: PropTypes.number.isRequired,
  isAutoplaying: PropTypes.bool.isRequired,
  isCancelledNextRecordingCountdown: PropTypes.bool.isRequired,
  onCancelNextRecording: PropTypes.func.isRequired,
  onReplay: PropTypes.func.isRequired,
  isTokenShared: PropTypes.bool.isRequired,
  hasPreviousRecording: PropTypes.bool,
  onPrevious: PropTypes.func.isRequired,
  hasNextRecording: PropTypes.bool,
  onTimerExpired: PropTypes.func,
  onNext: PropTypes.func.isRequired,
};
